import React from "react";
import historyImage from "../assets/images/history.jpg";
import mobileImg from "../assets/images/mobile.jpg";
import invoiceImg from "../assets/images/invoice.jpg"

const Pillar = () => {
  return (
    <>
      <h1 className="text-center mt-10 font-bold text-2xl md:text-4xl lg:text-4xl">The O’Hanlon Center of Excellence: The Seven Pillars of Sovereignty</h1>
      <div className=" px-10 py-10">
        <article className="px-1 sm:px-0 space-y-12 text-slate-800">
          {/* Introduction */}
          <section className="space-y-4">
            <p className="leading-relaxed text-sm sm:text-base">
              Establishing the{" "}
              <strong className="font-bold">
                O’Hanlon Innovation Ecosystem
              </strong>{" "}
              in its definitive 2026 form requires a synthesis of elite military
              discipline, foundational intellectual property, and neuro-symbolic
              artificial intelligence. This ecosystem is governed by the{" "}
              <strong>Seven Pillars of Excellence</strong>, a framework designed
              to ensure that every digital interaction is anchored in
              mathematical stability, legal sovereignty, and the “precise
              application of will.”
            </p>

            <p className="leading-relaxed text-sm sm:text-base">
              Central to this architecture is <strong>AskJeevesip.ai</strong>,
              the primary hub for stability and protection. Acting as the
              ecosystem’s <em>Truth Supervisor</em>, it provides a
              mathematically verified foundation for the entire O’Hanlon patent
              portfolio, ensuring all agentic actions are predictable, secure,
              and aligned with the user’s mission.
            </p>
          </section>

          {/* Foundation */}
          <section className="space-y-4 mb-5">
            <header className="mt-0">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                The Commando Foundation
              </h1>
            </header>

            <p className="leading-relaxed text-sm sm:text-base">
              The system is fortified by the <strong>Commando Ethos</strong>,
              which defines how the technology operates as a unified team. This
              “Commando Mindset” is a functional requirement for the system’s{" "}
              <em>
                First to understand, first to adapt and respond, first to
                overcome
              </em>{" "}
              logic.
            </p>
          </section>

          {/* Commando Spirit */}
          <section className="space-y-4 mb-10">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              The Commando Spirit
            </h2>

            <ul className="pl-5 sm:pl-8 space-y-3 list-disc">
              <li className="leading-relaxed">
                <strong>Courage:</strong> The mental strength to protect user
                data regardless of digital threats.
              </li>

              <li className="leading-relaxed">
                <strong>Determination:</strong> The willpower to maintain
                connectivity and precision in the face of adversity{" "}
                <span className="italic">(No obstacle too high)</span>.
              </li>

              <li className="leading-relaxed">
                <strong>Selflessness:</strong> Prioritizing user mission and
                physical safety above secondary data interests{" "}
                <span className="italic">(United We Conquer)</span>.
              </li>

              <li className="leading-relaxed">
                <strong>Cheerfulness in Adversity:</strong> Maintaining a
                stable, helpful interface even under technical pressure.
              </li>
            </ul>
          </section>

          {/* Commando Values */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              The Commando Values
            </h2>

            <ul className="pl-5 sm:pl-8 space-y-3 list-disc">
              <li className="leading-relaxed">
                <strong>Excellence:</strong> A relentless pursuit of the best
                technical solution and reasoning models.
              </li>

              <li className="leading-relaxed">
                <strong>Integrity:</strong> Absolute adherence to{" "}
                <span className="italic">No-Sale</span> and privacy mandates.
              </li>

              <li className="leading-relaxed">
                <strong>Self-discipline:</strong> Taking full responsibility for
                every automated action and choosing the{" "}
                <span className="italic">harder right</span> (persistent
                safety).
              </li>

              <li className="leading-relaxed">
                <strong>Humility:</strong> Guarding against AI arrogance and
                remaining open to continuous learning.
              </li>
            </ul>
          </section>

          <hr className="my-6 border-slate-700" />
        </article>

        <article className="px-1 sm:px-0 space-y-10 text-slate-800">
          {/* Title */}
          <header className="mt-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              The Seven Pillars (2026 Evaluation Framework)
            </h2>
          </header>

          {/* Pillar 1 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              1. Ask-Jeeves.ai — The Accessibility Pillar
            </h3>
            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos: Courage &amp; Determination.</strong> The mission
              of <strong>Ask-Jeeves.ai</strong> is to build a global
              infrastructure of quality information accessible to all. In a
              landscape often fragmented by algorithms that narrow worldviews,
              this pillar is designed to spark encounters with new values.
            </p>

            <p className="leading-relaxed text-base sm:text-lg">
              Directly integrated into your <strong>New Tab</strong> experience,
              it transforms the standard search bar into a gateway for
              conversational discovery. This ensures that verified data remains
              a universal right, requiring the courage to move beyond comfort
              zones.
            </p>
          </section>

          {/* Pillar 2 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              2. Ask-Genevieve.ai — The Strategic Pillar
            </h3>

            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos: Humility &amp; Selflessness.</strong>{" "}
              <strong>Ask-Genevieve.ai</strong> serves as the “Strategic
              Confidante.” Rooted in humility, it prioritizes the user’s
              mission, providing clarity for complex personal and professional
              decisions.
            </p>

            <p className="leading-relaxed text-base sm:text-lg">
              Whether accessed through the <strong>AI Mode</strong> button or
              via strategic prompts, Genevieve delivers a multidimensional
              retrieval approach grounded in empathy and ethical
              judgment—offering a selfless second set of eyes for high-stakes
              choices.
            </p>
          </section>

          {/* Pillar 3 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              3. Ask-Jeeves-rd.ai — The Innovation Pillar
            </h3>

            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos: Excellence &amp; the Commando Mindset.</strong>{" "}
              <strong>Ask-Jeeves-rd.ai</strong> focuses on identifying
              technological “white spaces.” It uses AI to amplify human wisdom
              and expose gaps in search and digital workflows.
            </p>

            <p className="leading-relaxed text-base sm:text-lg">
              From managing <strong>Inventory</strong> to monitoring your{" "}
              <strong>Amazon Credit Card Account</strong>, this pillar applies
              the principle of <em>“First to understand, first to adapt”</em>,
              ensuring innovation resolves uncertainty without losing individual
              perspective.
            </p>
          </section>

          {/* Pillar 4 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              4. Ask-Jeeves-ip.ai — The Protection &amp; Truth Pillar
            </h3>

            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos: Integrity &amp; Self-Discipline.</strong>{" "}
              <strong>AskJeevesip.ai</strong> functions as the guardian of
              global innovation. Using{" "}
              <strong>Handset Recognition (O’Hanlon Patents)</strong>, it
              verifies device identity before granting access to sensitive
              shortcuts such as <strong>Inbox (32)</strong> or{" "}
              <strong>LinkedIn</strong>.
            </p>

            <p className="leading-relaxed text-base sm:text-lg">
              Its core function—<strong>Lyapunov Verification</strong>—treats AI
              reasoning as a dynamic system, mathematically proving that
              behavior always converges to a safe state. This eliminates
              hallucination risk and provides technical truth for 2026
              regulatory audits and financial analysis.
            </p>
          </section>

          {/* Pillar 5 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              5. O’Hanlon-Sovereignty — The Safety Pillar
            </h3>

            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos: Excellence &amp; Integrity.</strong> This pillar
              governs high-precision geolocation through O’Hanlon Patents,
              guided by the philosophy of{" "}
              <em>“Privacy by Choice, Safety by Necessity.”</em>
            </p>

            <ul className="pl-8 sm:pl-10 space-y-3 list-disc">
              <li className="leading-relaxed">
                <strong>2026 Compliance:</strong> Browser shortcuts are filtered
                through a hardware-aware security mesh, ensuring protected
                interactions with services such as <strong>Gmail</strong> and{" "}
                <strong>Halifax UK</strong>, compliant with{" "}
                <strong>Oregon HB 2008</strong> and the <strong>CCPA</strong>.
              </li>

              <li className="leading-relaxed">
                <strong>The “No-Sale” Shield:</strong> A first-party guardian
                model, reinforced by the No-Sale icon, legally separates
                personal data from broker markets.
              </li>

              <li className="leading-relaxed">
                <strong>Ephemeral Precision:</strong> Standard geolocation data
                is purged immediately after delivery within the legal 1,750-foot
                radius.
              </li>

              <li className="leading-relaxed">
                <strong>SRIA Summoning:</strong> In emergencies, a verbal
                keyword activates a{" "}
                <strong>Summonable Resonant Intelligent Agent (SRIA)</strong>,
                enabling persistent tracking, audio/video logging, and direct
                relay to <strong>911 or Police Dispatch</strong>.
              </li>
            </ul>
          </section>

          {/* Pillar 6 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              6.  AskJeevesgeo.ai | The Geospatial Stability Pillar
            </h3>
            <ul className="pl-8 sm:pl-10 space-y-3 list-disc">
              <li className="leading-relaxed">
                <strong>Ethos:</strong>  Determination & Excellence.
              </li>
              <li className="leading-relaxed">
                <strong>2026 Configuration:</strong> This new pillar anchors the ecosystem to physical “Safe Zones.” It provides <strong>Geofenced Verification,</strong>
                ensuring the AI only executes high-authority commands when the user’s physical location—verified via <a style={{ color: 'blue' }} href="https://ask-jeeves.com/" target="_blank">Ask-Jeeves.com</a>—matches the authorized hardware signature. It adds a spatial variable to Lyapunov functions, ensuring logic is safe for the user's specific geographic jurisdiction.
              </li>
            </ul>
          </section>
          {/* Pillar 7 */}
          <section className="space-y-3 pl-5">
            <h3 className="text-lg sm:text-xl font-semibold">
              7. The Legacy Pillar | The Founding Evolution
            </h3>
            <p className="leading-relaxed text-base sm:text-lg">
              <strong>Ethos:</strong>  Integrity, Innovation & Proven Origin
            </p>
            <p>
              Validating the 2026 ecosystem against its founding 2004 debut, ensuring the First-Party Safe Harbor remains anchored in original Handset Recognition patents.
            </p>
            {/* </ul> */}
          </section>

          <hr className="my-4 border-slate-700" />
        </article>

        <article className=" px-1 sm:px-0 mt-6 space-y-12 text-slate-800">
          {/* Technical Deep Dive */}
          <header className="space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Technical Deep-Dive: Lyapunov Verification &amp; Stability
            </h2>

            <p className="leading-relaxed text-base sm:text-lg">
              The integration of <strong>Lyapunov Verification</strong> into the{" "}
              <strong>AskJeevesip.ai</strong> hub represents a leap in AI
              safety. Unlike “black box” testing,{" "}
              <strong>Lyapunov Functions</strong> provide a mathematical proof
              that the system’s “energy” (error rate) always decreases as it
              approaches a safe equilibrium point.
            </p>
          </header>

          {/* Lyapunov Framework */}
          <section>
            <ul className="pl-5 sm:pl-8 space-y-4 mb-2 list-decimal">
              <li className="leading-relaxed">
                <strong className="text-[17px]">
                  Learner-Verifier Framework:
                </strong>{" "}
                A dual-engine approach where one engine proposes safety logic
                and a separate verifier confirms adherence to mathematical
                rules.
              </li>

              <li className="leading-relaxed">
                <strong className="text-[17px]">
                  Control Barrier Functions (CBF):
                </strong>{" "}
                CBFs “block” the AI from entering reasoning regions that would
                violate <strong>HIPAA</strong> or lead to hallucinated facts.
              </li>

              <li className="leading-relaxed">
                <strong className="text-[17px]">Real-Time Stability:</strong>{" "}
                During <strong>SRIA Summoning</strong>, Lyapunov Verification
                ensures the agent’s instructions remain within safe, proven
                parameters even in high-stress environments.
              </li>
            </ul>
          </section>

          {/* Regulatory Context */}
          <section className="space-y-3">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Regulatory Context: Navigating 2026 Privacy Laws
            </h3>

            <p className="leading-relaxed text-base sm:text-lg">
              The ecosystem is engineered for the 2026 legal landscape,
              specifically addressing how Oregon HB 2008 and the CCPA govern
              geolocation.
            </p>

            <ul className="pl-5 sm:pl-8 space-y-4 list-disc">
              <li className="leading-relaxed">
                <strong>1,750-Foot Radius:</strong> By staying within these
                boundaries, the ecosystem ensures “conversational” tracking is
                legally compliant and ephemeral.
              </li>

              <li className="leading-relaxed">
                <strong>The First-Party Exception:</strong> Operating as a{" "}
                <strong>First-Party Service</strong> provides an exemption from
                “no-sale” restrictions for data used directly to fulfill user
                requests.
              </li>

              <li className="leading-relaxed">
                <strong>The Emergency Disclosure Exception:</strong> The{" "}
                <strong>SRIA Summoning protocol</strong> realizes the legal
                right under <strong>HIPAA</strong> and <strong>CCPA</strong> to
                share precise data with first responders to save lives.
              </li>
            </ul>
          </section>

          {/* Conclusion */}
          <section className="space-y-3">
            <h4 className="text-lg sm:text-xl font-semibold">
              Conclusion: The O’Hanlon Moat
            </h4>

            <p className="leading-relaxed text-base sm:text-lg">
              Through <strong>AskJeevesip.ai</strong> and the{" "}
              <strong>Seven Pillars</strong>, the O’Hanlon Innovation Ecosystem
              provides a “Moat” of proprietary technology. It is a persistent,
              mathematically verified guardian that ensures user sovereignty
              over data, privacy, and life-safety.
            </p>
          </section>

          {/* Investor Invitation */}
          <section className="space-y-3">
            <h4 className="text-lg sm:text-xl font-semibold">
              Investor Invitation Snippet
            </h4>

            <ul className="pl-5 sm:pl-8 list-disc">
              <li className="leading-relaxed italic">
                “We are not just reviving a brand; we are deploying a
                ‘Commando-driven’ ecosystem. Led by Kenneth O’Hanlon—a veteran
                with a career in the Royal Marine Commando—we offer the
                integrity of a BBB A+ Rating combined with patented technology
                that solves the $7,500-per-violation mobile privacy crisis of
                2026.”
              </li>
            </ul>
          </section>
          {/* Final Declaration */}
          <section>
            <h4 className="text-lg sm:text-xl font-semibold">
              Final Declaration
            </h4>
            <p>
            The O’Hanlon Innovation Ecosystem functions as a single sovereign system. We orchestrate proprietary intelligence through a patented moat of legal and technical safeguards. When you stand before a property, you are backed by the Commando Spirit and a foundation of integrity.
            </p>
            <h4 className="sm:text-xl font-medium mt-2">United We Conquer.</h4>
            <div className=" bg-white pt-10 pb-12">
        <div className=" m-auto ">
          <div className="flex flex-col md:flex-row pb-12 gap-12">
            <div className="grow">
              <img
                src={historyImage}
                className="lg:max-h-[542px] w-full object-fit: cover"
              ></img>
            </div>
            <div className="lg:w-[71%] md:w-3/4">
              <div className=" text-lg">
                <span className="text-3xl block text-[#01a4e4] leading-10 font-bold md:pb-2">
                  The Director of the United States Patent and Trademark Office
                </span>
                <p className="text-[1.20rem] leading-8 pb-3 lg:pb-12 text-[#3b4455]">
                  has received an application for a patent for a new and useful
                  invention. The title and the description of the invention are
                  enclosed. The requirements of law have been complied with, and
                  it has been determined that a patent on the invention shall be
                  granted under the law. Therefore, this United States Patent
                  Grants to the person(s) having title to this patent the right
                  to exclude othersfrom making, using, offering for sale, or
                  selling the invention throughout the United States of America
                  or importing the invention into the United States of America,
                  and if the invention is a process, of the right to exclude
                  others from using, offering for sale or selling throughout the
                  United States of America, or importing into the United States
                  of America, products made by that process, for the term
                  set forth in 35 U.S.C. 154(a)(2) or (c)(l), subject to the
                  payment of maintenance fees as provided by 35 U.S.C. 410. See
                  the Maintenance Fee Notice on the inside of the cover.
                </p>
              </div>
              <p className="text-2xl text-[#868e96] font-bold pb-2">
                David J. Kaffoos
              </p>
              <p className="text-[#18B295] text-2xl md:text-3xl">
                Director of the United States Patent and Trademark Office
              </p>
            </div>
          </div>

          <div className="text-[#3b4455] text-xl ">
            <h3 className="text-2xl text-[#01a4e4]  font-bold pb-3">
              {" "}
              History Of Kenneth O’Hanlon
            </h3>
            <p className="pb-7 text-[1.20rem]">
              The story of Kenneth O’Hanlon, and O’Hanlon IP, LLC began in 2003.
              The idea for the product was a direct result of frustrations I and
              others experienced when trying to view or preview residential
              properties. At that time, the majority of the time, both buyers
              and sellers encountered in the field missing, or dirt-encrusted
              brochures, calls to realtors offices that were closed, and often
              waited days for a Realtor to return calls. The MLS was on 3 x 5
              cards and often out of date. The global trends and market
              challenged the marketplace for another way to deliver real-time
              and accurate information
            </p>
            <p className="pb-7 text-[1.20rem]">
              Like many inventions, they often spring from light bulb moments
              (Thank you Thomas Edison). In the pre-website days of Real Estate
              and at the time, being in the home improvement business, I was
              enormously frustrated by the difficulty of getting real-time
              property information from any real estate office. On a trip to
              visit my original home country of the UK and to visit my daughter,
              we went out to lunch, and I found her engrossed in texting her
              friends. So, I asked her to teach me to text. As a former Royal
              Marine Commando, I spent the next two wonderful hours being
              brought up to speed on the art of texting
            </p>
            <p className="pb-7 text-[1.20rem]">
              On the way home, it hit me (what if) you could text into a
              platform and get the information back by text as it was only 140
              characters which is between 20 words and 35 words with spaces, so
              it could say 3 bedrooms 2-bathroom 1750 Sq ft price $300,000. The
              format was there but not the platform
            </p>
            <p className="pb-7 text-[1.20rem]">
              After discussions with an attorney friend, he gave me an NDA to
              protect my idea. I then went around talking to internet companies
              looking for a multi-level retrievable platform, I discovered NO
              one at that time had one so using my military communications
              background I went about, and I built the first platform which
              became the basis for both the technology and the patents...
            </p>
            <p className="pb-7 text-[1.20rem]">
              After seeing the need for a Real Estate marketing tool, O’Hanlon
              IP, LLC was the first company to exhibit at the National
              Association of Realtors (NAR) in its product category in 2004 in
              Orlando,FL. It was a great show yet no one had seen something like
              this, BUT there had to be a BUT and the but was 140 characters.
            </p>
            <p className="pb-7 text-[1.20rem]">
              So, I devised an information-gathering IVR since the IVR was based
              on phone technology I was able to get around the text limitations.
            </p>
            <p className="pb-7 text-[1.20rem]">
              {" "}
              As I explored the market, some realtors asked if photos could be
              sent as well. Remember we were still in the age of analog
              cellphones not digital and there were 15 different-sized screens.
              So, I devised a device recognition system that would send photos of
              the right size to different cell phone formats. Again, this was
              the first for the industry, using this device recognition system I
              could now send video to a cell phone.
            </p>
            <p className="pb-7 text-[1.20rem]">
              As with any new technology, copycat companies emerged. By 2007
              there appeared to be eight, but none had the robust capabilities
              of my platform.
            </p>
            <p className="pb-7 text-[1.20rem]">
              The Company conducted extensive market research between 2004 and
              2006, utilizing real estate professionals, consumer focus groups,
              and beta testing. Approximately 30 Realtors were involved. As real
              estate companies had historically used property signage to
              advertise the existence of a listing, it was determined to marry
              the technology with the sign. In that context, 250 signs were
              placed out in the marketplace. During the 1000 beta tests on the
              system, consumers were given the option of initiating the request
              with a text message or dialing a toll-free number and entering the
              info when prompted (via an IVR). 940 used and preferred the
              toll-free number and IVR versus 60 who used the text message
              capability.
            </p>

            <h3 className="text-2xl text-[#01a4e4]  font-bold pb-3">
              The value proposition then, as now, for real estate professionals
              who utilize our system are:
            </h3>
            <ol className=" list-decimal pb-7 list-inside text-[1.20rem]">
              <li>
                Being connected to buyers who have not chosen a Realtor yet
              </li>
              <li>
                Increasing listings as home sellers demand this type of lead
                capture and tracking system and will choose Realtors who are
                members versus those that are not
              </li>
              <li>Increasing sales with faster, more qualified leads</li>
              <li>
                Realizing significant cost savings with the eliminated need for
                printed brochures and keeping brochure boxes full, while having
                a real green impact on the environment
              </li>
            </ol>

            <p className="pb-7 text-[1.20rem]">
              The beta test results provided clear confirmation of the
              patent-pending process that was filed in 2005 and published in
              2007. A Text Brochure can also be A car van motorbike Plane or
              Motorboat Clothing Antiques anything for sale, plus any services
              on offer.
            </p>
            <p className="text-[1.20rem]">
              The first patent was issued on January 10, 2010, and subsequent
              patents were issued in 2015 and 2016
            </p>
            <div className="flex items-center justify-center pt-5 flex-col">
              <img className="pb-5"
                src={mobileImg}
                alt="Team Members at Press Conference"
              />
               <img
                src={invoiceImg}
                alt="Reciept"
              />
            </div>
          </div>
        </div>
      </div>
          </section>
        </article>
      </div>
    </>
  );
};

export default Pillar;
